import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Eye, ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import { ImageUpload } from '../../components/shared/ImageUpload';

export default function TestSeriesManagement() {
  const navigate = useNavigate();
  const [series, setSeries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showTrash, setShowTrash] = useState(false);
  const [trash, setTrash] = useState([]);
  const [form, setForm] = useState({
    title: '', category_id: '', description: '', price: 0, discount_price: 0,
    validity_months: 12, thumbnail_url: '', is_published: false
  });

  useEffect(() => { loadCategories(); }, []);
  useEffect(() => { loadSeries(); }, [page, limit]);

  async function loadCategories() {
    try { const { data } = await api.get('/admin/categories'); setCategories(data); } catch {}
  }

  async function loadSeries() {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/test-series?page=${page}&limit=${limit}`);
      setSeries(data.items || []);
      setTotalPages(data.pages || 1);
    } catch {} finally { setLoading(false); }
  }

  async function loadTrash() {
    try { const { data } = await api.get('/admin/trash'); setTrash(data); } catch {}
  }

  function openCreate() {
    setEditing(null);
    setForm({ title: '', category_id: categories[0]?.id || '', description: '', price: 0, discount_price: 0, validity_months: 12, thumbnail_url: '', is_published: false });
    setDialogOpen(true);
  }

  function openEdit(s) {
    setEditing(s);
    setForm({ title: s.title, category_id: s.category_id || '', description: s.description || '', price: s.price || 0, discount_price: s.discount_price || 0, validity_months: s.validity_months || 12, thumbnail_url: s.thumbnail_url || '', is_published: s.is_published || false });
    setDialogOpen(true);
  }

  async function handleSave() {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    try {
      if (editing) {
        await api.put(`/admin/test-series/${editing.id}`, form);
        toast.success('Updated');
      } else {
        await api.post('/admin/test-series', form);
        toast.success('Created');
      }
      setDialogOpen(false);
      loadSeries();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Move to trash?')) return;
    try { await api.delete(`/admin/test-series/${id}`); toast.success('Moved to trash'); loadSeries(); } catch { toast.error('Failed'); }
  }

  async function handleRestore(id) {
    try { await api.post(`/admin/test-series/${id}/restore`); toast.success('Restored'); loadTrash(); loadSeries(); } catch { toast.error('Failed'); }
  }

  async function handlePermanentDelete(id) {
    if (!window.confirm('Permanently delete? This cannot be undone.')) return;
    try { await api.delete(`/admin/test-series/${id}/permanent`); toast.success('Permanently deleted'); loadTrash(); } catch { toast.error('Failed'); }
  }

  return (
    <div data-testid="test-series-management">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-['Outfit'] text-2xl font-semibold text-[#0A0A0A]">{showTrash ? 'Trash Bin' : 'Test Series'}</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setShowTrash(!showTrash); if (!showTrash) loadTrash(); }} data-testid="toggle-trash-btn">
            {showTrash ? <><ChevronLeft size={14} className="mr-1" /> Back</> : <><Trash2 size={14} className="mr-1" /> Trash</>}
          </Button>
          {!showTrash && (
            <Button className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white" onClick={openCreate} data-testid="create-series-btn">
              <Plus size={16} className="mr-2" /> Create Series
            </Button>
          )}
        </div>
      </div>

      {showTrash ? (
        <Card className="border border-zinc-200 bg-white overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Deleted At</TableHead><TableHead className="w-[120px]">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {trash.length === 0 ? (
                <TableRow><TableCell colSpan={3} className="text-center py-8 text-[#52525B]">Trash is empty</TableCell></TableRow>
              ) : trash.map(s => (
                <TableRow key={s.id}><TableCell>{s.title}</TableCell><TableCell className="text-sm text-[#52525B]">{s.deleted_at ? new Date(s.deleted_at).toLocaleDateString() : '-'}</TableCell>
                  <TableCell><div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleRestore(s.id)} data-testid={`restore-${s.id}`}><RotateCcw size={14} /></Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handlePermanentDelete(s.id)} data-testid={`perm-delete-${s.id}`}><Trash2 size={14} /></Button>
                  </div></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#52525B]">Rows per page:</span>
              <Select value={String(limit)} onValueChange={v => { setLimit(Number(v)); setPage(1); }}>
                <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
                <SelectContent>{[10, 20, 30, 40, 50, 100].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-[#52525B]">Page {page}/{totalPages}</span>
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={14} /></Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight size={14} /></Button>
            </div>
          </div>

          <Card className="border border-zinc-200 bg-white overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Tests</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[140px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8">Loading...</TableCell></TableRow>
                ) : series.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#52525B]">No test series found</TableCell></TableRow>
                ) : series.map(s => (
                  <TableRow key={s.id} data-testid={`series-row-${s.id}`}>
                    <TableCell className="font-medium">{s.title}</TableCell>
                    <TableCell className="text-sm">{s.category_name || '-'}</TableCell>
                    <TableCell className="text-sm">{s.discount_price > 0 && s.discount_price < s.price ? <><span className="price-original mr-1">&#8377;{s.price}</span> &#8377;{s.discount_price}</> : s.price > 0 ? `₹${s.price}` : 'Free'}</TableCell>
                    <TableCell>{s.total_tests || 0}</TableCell>
                    <TableCell><Badge className={s.is_published ? 'bg-green-100 text-green-700 border-0' : 'bg-zinc-100 text-zinc-500 border-0'}>{s.is_published ? 'Published' : 'Draft'}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/test-series/${s.id}/tests`)} data-testid={`manage-tests-${s.id}`}><Eye size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`edit-series-${s.id}`}><Pencil size={14} /></Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(s.id)} data-testid={`delete-series-${s.id}`}><Trash2 size={14} /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Outfit']">{editing ? 'Edit Test Series' : 'Create Test Series'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="series-title-input" /></div>
            <div><Label>Category</Label>
              <Select value={form.category_id} onValueChange={v => setForm({ ...form, category_id: v })}>
                <SelectTrigger data-testid="series-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} data-testid="series-desc-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Price (&#8377;)</Label><Input type="number" value={form.price} onChange={e => setForm({ ...form, price: Number(e.target.value) })} data-testid="series-price-input" /></div>
              <div><Label>Discount Price</Label><Input type="number" value={form.discount_price} onChange={e => setForm({ ...form, discount_price: Number(e.target.value) })} data-testid="series-discount-input" /></div>
            </div>
            <div><Label>Validity (months)</Label><Input type="number" value={form.validity_months} onChange={e => setForm({ ...form, validity_months: Number(e.target.value) })} /></div>
            <div><Label>Thumbnail</Label><ImageUpload value={form.thumbnail_url} onChange={v => setForm({ ...form, thumbnail_url: v })} folder="thumbnails" /><Input value={form.thumbnail_url} onChange={e => setForm({ ...form, thumbnail_url: e.target.value })} placeholder="Or paste URL..." className="mt-2 text-xs" data-testid="series-thumb-input" /></div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_published} onCheckedChange={v => setForm({ ...form, is_published: v })} data-testid="series-publish-switch" />
              <Label>Published</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white" onClick={handleSave} data-testid="save-series-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
